// frontend/src/api/photoInbox.ts
/**
 * API client for a tank's photo inbox (/api/tanks/{tankId}/photo-inbox).
 * See docs/plans/2026-10-13-photo-inbox.md, section 9, and
 * backend/app/api/photo_inbox.py. Response shapes live in @/types/photoInbox.
 *
 * Errors: a 429 rejects with RateLimitError (from Retry-After; this includes
 * `too_many_in_flight` from an upload or a cropped assign), a request that
 * never reaches the server with PhotoInboxNetworkError, an upload whose signal
 * was aborted with PhotoUploadCancelledError, and any other non-2xx with a PhotoInboxError
 * carrying the status. 409s whose detail is a coded object become
 * PhotoInboxConflictError (the queue counts a duplicate as done and stops on
 * inbox_full; assigned_elsewhere says where the photo went instead; a 409 with
 * a string detail stays a PhotoInboxError), a 503
 * `busy` (image processing saturated) PhotoInboxBusyError with its
 * Retry-After, 413 PhotoTooLargeError and 400 PhotoRejectedError.
 */

import { apiClient } from './client';
import { buildApiUrl } from './config';
import { getLargePhoto } from './largePhoto';
import { parseRetryAfter, RateLimitError } from './rateLimit';
import { getCachedSession, supabase } from '@/lib/supabase';
import type {
  AssignTarget,
  PhotoInboxAssignRequest,
  PhotoInboxAssignResult,
  PhotoInboxCheckResponse,
  PhotoInboxConflict,
  PhotoInboxConflictCode,
  PhotoInboxCount,
  PhotoInboxDetail,
  PhotoInboxDiscardResponse,
  PhotoInboxItem,
  PhotoInboxListParams,
  PhotoInboxPage,
  PhotoInboxStatus,
  PhotoInboxUpload,
} from '@/types/photoInbox';
import type { LargePhoto } from '@/types/tankPhotos';

/** A non-2xx answer from an inbox route (other than 429), or no answer at all (status 0). */
export class PhotoInboxError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'PhotoInboxError';
    this.status = status;
  }
}

/** The request never got an answer: offline, connection dropped, or timed out. */
export class PhotoInboxNetworkError extends PhotoInboxError {
  constructor() {
    super(0, 'Network error');
    this.name = 'PhotoInboxNetworkError';
  }
}

/** 409 with a coded detail (duplicate, on_livestock, inbox_full, ...). */
export class PhotoInboxConflictError extends PhotoInboxError {
  readonly conflict: PhotoInboxConflict;

  constructor(conflict: PhotoInboxConflict) {
    super(409, conflict.message);
    this.name = 'PhotoInboxConflictError';
    this.conflict = conflict;
  }
}

/** 413: the photo is over the server's (or nginx's) size limit. */
export class PhotoTooLargeError extends PhotoInboxError {
  constructor(message: string) {
    super(413, message);
    this.name = 'PhotoTooLargeError';
  }
}

/** 400: the server rejected the photo or a field (not an image, too many pixels, ...). */
export class PhotoRejectedError extends PhotoInboxError {
  constructor(message: string) {
    super(400, message);
    this.name = 'PhotoRejectedError';
  }
}

/** The wait after a 503 busy without a usable Retry-After (the server sends 10). */
export const DEFAULT_BUSY_WAIT_S = 10;

/**
 * 503 `busy`: the server's image-processing slots are all in use (upload, and
 * assign with a crop). Try the same request again after `retryAfterSeconds`
 * (DEFAULT_BUSY_WAIT_S when it is null).
 */
export class PhotoInboxBusyError extends PhotoInboxError {
  /** From Retry-After; null when it was missing or unreadable. */
  readonly retryAfterSeconds: number | null;

  constructor(message: string, retryAfter: string | null) {
    super(503, message);
    this.name = 'PhotoInboxBusyError';
    this.retryAfterSeconds = parseRetryAfter(retryAfter);
  }
}

/** The upload's AbortSignal was aborted (sign-out): it was stopped, or never sent. */
export class PhotoUploadCancelledError extends Error {
  constructor() {
    super('Upload cancelled');
    this.name = 'PhotoUploadCancelledError';
  }
}

/** A stalled upload on a phone connection otherwise never ends; 9 MB at 0.3 Mbit/s fits. */
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

const STATUSES: ReadonlySet<string> = new Set<PhotoInboxStatus>([
  'uploading',
  'pending',
  'assigning',
  'assigned',
  'discarded',
  'failed',
]);

const PLAIN_CONFLICT_CODES: ReadonlySet<string> = new Set<PhotoInboxConflictCode>([
  'inbox_full',
  'cancelled',
  'in_progress',
  'not_pending',
  'expired',
  'uploaded_again',
  'gone',
]);

function isTarget(value: unknown): value is AssignTarget {
  return value === 'organism' || value === 'tank';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The 409 detail as a typed conflict, or null for a code (or shape) this client doesn't know. */
function parseConflict(detail: unknown): PhotoInboxConflict | null {
  if (!isRecord(detail) || typeof detail.code !== 'string' || typeof detail.message !== 'string') {
    return null;
  }
  const { code, message } = detail;
  if (code === 'duplicate' && typeof detail.id === 'number') {
    const status =
      typeof detail.status === 'string' && STATUSES.has(detail.status)
        ? (detail.status as PhotoInboxStatus)
        : null;
    return { code, message, id: detail.id, status };
  }
  if (code === 'on_livestock' && typeof detail.livestock_name === 'string') {
    return {
      code,
      message,
      livestock_name: detail.livestock_name,
      ...(isTarget(detail.target) && { target: detail.target }),
    };
  }
  if (code === 'assigned_elsewhere' && isTarget(detail.target)) {
    return {
      code,
      message,
      target: detail.target,
      livestock_name: typeof detail.livestock_name === 'string' ? detail.livestock_name : null,
    };
  }
  if (PLAIN_CONFLICT_CODES.has(code)) {
    return { code: code as PhotoInboxConflictCode, message };
  }
  return null;
}

/** A readable message from a FastAPI `detail`: a string, a coded object, or a 422 error list. */
function detailMessage(detail: unknown): string | null {
  if (typeof detail === 'string' && detail) return detail;
  if (isRecord(detail) && typeof detail.message === 'string') return detail.message;
  if (Array.isArray(detail) && isRecord(detail[0]) && typeof detail[0].msg === 'string') {
    return detail[0].msg;
  }
  return null;
}

/** The typed error for a non-2xx response body (already parsed, or null if it wasn't JSON). */
function errorForResponse(
  status: number,
  body: unknown,
  retryAfter: string | null
): PhotoInboxError {
  const detail = isRecord(body) ? body.detail : undefined;
  if (status === 409) {
    const conflict = parseConflict(detail);
    if (conflict) return new PhotoInboxConflictError(conflict);
  }
  const message = detailMessage(detail);
  if (status === 503 && isRecord(detail) && detail.code === 'busy') {
    return new PhotoInboxBusyError(message ?? 'The server is busy', retryAfter);
  }
  if (status === 413) return new PhotoTooLargeError(message ?? 'Too large');
  if (status === 400) return new PhotoRejectedError(message ?? 'The photo was rejected');
  // A proxy's own 502/504 page (the backend down or too slow) has no detail to show.
  if (status >= 500 && !message) return new PhotoInboxError(status, 'Server error, try again');
  return new PhotoInboxError(status, message ?? `HTTP ${status}`);
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** JSON request through apiClient (token, 401 refresh, 429), with the inbox's error types. */
async function request<T>(endpoint: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await apiClient(endpoint, init);
  } catch (error) {
    // fetch rejects with a TypeError when the request never got a response
    if (error instanceof TypeError) throw new PhotoInboxNetworkError();
    throw error;
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw errorForResponse(response.status, body, response.headers.get('Retry-After'));
  }
  return response.json() as Promise<T>;
}

function base(tankId: string): string {
  return `/api/tanks/${encodeURIComponent(tankId)}/photo-inbox`;
}

interface XhrResult {
  status: number;
  text: string;
  retryAfter: string | null;
}

/** Sends the form with the token current at send time; resolves with any HTTP answer. */
function sendForm(
  url: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal
): Promise<XhrResult> {
  return new Promise<XhrResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new PhotoUploadCancelledError());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', buildApiUrl(url));
    xhr.timeout = UPLOAD_TIMEOUT_MS;

    const session = getCachedSession();
    if (session?.access_token) {
      xhr.setRequestHeader('Authorization', `Bearer ${session.access_token}`);
    }

    if (onProgress) {
      xhr.upload.onprogress = (event): void => {
        if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
      };
    }

    const abort = (): void => xhr.abort();
    signal?.addEventListener('abort', abort, { once: true });
    const settled = (): void => signal?.removeEventListener('abort', abort);

    xhr.onload = (): void => {
      settled();
      resolve({
        status: xhr.status,
        text: xhr.responseText,
        retryAfter: xhr.getResponseHeader('Retry-After'),
      });
    };
    const failed = (): void => {
      settled();
      reject(signal?.aborted ? new PhotoUploadCancelledError() : new PhotoInboxNetworkError());
    };
    xhr.onerror = failed;
    xhr.ontimeout = failed;
    xhr.onabort = failed;
    xhr.send(form);
  });
}

/** One refresh shared by concurrent uploads that all got a 401. */
let refreshing: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  refreshing ??= supabase.auth
    .refreshSession()
    .then(({ error }) => !error)
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

export const photoInboxApi = {
  /** Which of these content hashes (at most 200) the tank has in its inbox or on its livestock. */
  check(tankId: string, hashes: string[]): Promise<PhotoInboxCheckResponse> {
    return request(`${base(tankId)}/check`, { method: 'POST', body: JSON.stringify({ hashes }) });
  },

  /**
   * Uploads one photo (multipart, through XMLHttpRequest for progress).
   * `onProgress` gets the fraction sent, 0 to 1. On a 401 the session is
   * refreshed once and the photo sent again; the page is never redirected,
   * so a running queue isn't lost. Aborting `signal` stops the request
   * (PhotoUploadCancelledError).
   */
  async upload(
    tankId: string,
    data: PhotoInboxUpload,
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal
  ): Promise<PhotoInboxItem> {
    const form = new FormData();
    form.append('photo', data.photo, data.filename);
    form.append('content_hash', data.content_hash);
    if (data.taken_at_local) form.append('taken_at_local', data.taken_at_local);
    if (data.taken_at_offset) form.append('taken_at_offset', data.taken_at_offset);
    if (data.file_last_modified != null) {
      form.append('file_last_modified', String(Math.round(data.file_last_modified)));
    }

    let result = await sendForm(base(tankId), form, onProgress, signal);
    if (result.status === 401 && (await refreshSession())) {
      result = await sendForm(base(tankId), form, onProgress, signal);
    }

    if (result.status >= 200 && result.status < 300) {
      return JSON.parse(result.text) as PhotoInboxItem;
    }
    if (result.status === 429) throw new RateLimitError(result.retryAfter);
    throw errorForResponse(result.status, parseJson(result.text), result.retryAfter);
  },

  /** Pending photos, oldest capture date first, with signed thumbnail URLs. */
  list(tankId: string, params: PhotoInboxListParams = {}): Promise<PhotoInboxPage> {
    const search = new URLSearchParams();
    if (params.after) search.set('after', params.after);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    const query = search.toString();
    return request(`${base(tankId)}${query ? `?${query}` : ''}`, { method: 'GET' });
  },

  count(tankId: string): Promise<PhotoInboxCount> {
    return request(`${base(tankId)}/count`, { method: 'GET' });
  },

  /** One pending photo with its 1920 px URL and neighbours. 404 once it is not pending. */
  get(tankId: string, id: number): Promise<PhotoInboxDetail> {
    return request(`${base(tankId)}/${id}`, { method: 'GET' });
  },

  /** A pending photo's master for the zoomed viewer; null when it isn't bigger than 1920 px. */
  large(tankId: string, id: number): Promise<LargePhoto | null> {
    return getLargePhoto(`${base(tankId)}/${id}/large`);
  },

  /**
   * Makes the photo a timeline entry or a tank shot (body.target); returns it,
   * also when the photo was assigned to that target already.
   */
  assign(
    tankId: string,
    id: number,
    body: PhotoInboxAssignRequest
  ): Promise<PhotoInboxAssignResult> {
    return request(`${base(tankId)}/${id}/assign`, { method: 'POST', body: JSON.stringify(body) });
  },

  discard(tankId: string, id: number): Promise<PhotoInboxDiscardResponse> {
    return request(`${base(tankId)}/${id}/discard`, { method: 'POST' });
  },

  /** Undo an assign or discard from the last 15 minutes; returns the photo, back in the inbox. */
  restore(tankId: string, id: number): Promise<PhotoInboxItem> {
    return request(`${base(tankId)}/${id}/restore`, { method: 'POST' });
  },
};
